<template>
  <!-- START Subnav -->
  <div v-if="getCurrentApplicationTemplate">
    <ApplySubNav
      active="account"
      :verify="getCurrentApplicationTemplate.need_applicant_experian_authtoken"
      :pay="getCurrentApplicationTemplate.price > 0"
      :income="getCurrentApplicationTemplate.need_applicant_finicity_bank_connection"
    />
    <!-- END Subnav -->

    <div class="h-full bg-brand-light-gray py-10" style="min-height: 95vh">
      <div class="max-w-screen-md mx-auto">
        <!-- START CREATE ACCOUNT SECTION -->
        <template v-if="['create', 'create_passcode'].includes(currentView)">
          <SectionContainer>
            <h2 class="text-xl font-semibold my-3">Would you like to set a password</h2>
            <p class="">
              Save time when accessing your application to modify it or add additional attachments.
            </p>
            <div class="py-3">
              <p class="">Enter a password below and we'll create an account using your email:</p>
            </div>

            <div class="py-3" v-if="applicantEmailFromForm">
              <p class="font-bold">
                {{ applicantEmailFromForm }}
              </p>
            </div>

            <div class="py-3" v-if="!applicantEmailFromForm">
              <label class="block"> Email </label>
              <SingleLineInput
                placeholder="Email"
                type="email"
                :value="email"
                @input="updateEmail"
              ></SingleLineInput>
            </div>

            <div class="py-3 w-full">
              <label class="block"> Password </label>
              <div class="w-full relative">
                <EyeIcon
                  v-if="!showPassword"
                  class="absolute h-7 w-7 mt-4 text-brand-green right-5 z-50 cursor-pointer"
                  @click="toggleShowPassword"
                />
                <EyeOffIcon
                  v-if="showPassword"
                  class="absolute h-7 w-7 mt-4 text-brand-dark-gray right-5 z-50 cursor-pointer"
                  @click="toggleShowPassword"
                />

                <SingleLineInput
                  id="password"
                  placeholder="Enter password here"
                  :type="showPassword ? 'text' : 'password'"
                  :value="password"
                  @input="updatePassword"
                ></SingleLineInput>
              </div>
            </div>

            <PasswordGuide :password="password" ref="passwordGuide" />

            <div class="mx-auto w-80 pt-5">
              <div class="py-2" v-if="errorMessage">
                <p class="text-brand-red text-sm">{{ errorMessage }}</p>
              </div>

              <div class="py-3">
                <PrimaryButton
                  @click="createAccount"
                  :disabled="!passwordValid || isApplicationTemplateLoading"
                  :is-loading="isApplicationTemplateLoading"
                  >Set password
                </PrimaryButton>
              </div>
              <div class="py-3">
                <SecondaryButton
                  @click="skipCreate"
                  :disabled="isApplicationTemplateLoading"
                  :is-loading="isApplicationTemplateLoading"
                  >Not now
                </SecondaryButton>
              </div>
            </div>
          </SectionContainer>
        </template>
        <!-- END CREATE ACCOUNT SECTION -->

        <!-- START LOGIN SECTION -->
        <template v-if="currentView === 'login'">
          <SectionContainer v-if="!isUserLoading && getCurrentCheckedUsernameInfo">
            <h2 class="text-xl font-semibold my-3">
              Welcome back
              {{ getCurrentCheckedUsernameInfo.first_name }}
            </h2>
            <p class="">
              Your email address,
              <span class="font-semibold">{{ getCurrentCheckedUsernameInfo.email }}</span>
              was used to apply to
              <span class="font-semibold">{{ getCurrentCheckedUsernameInfo.unit }}</span>
              in
              <span class="font-semibold">{{ getCurrentCheckedUsernameInfo.date_applied }}</span
              >. Do you remember your password?
            </p>

            <div class="py-3 w-full">
              <label class="block"> Password </label>
              <div class="w-full relative">
                <EyeIcon
                  v-if="!showPassword"
                  class="absolute h-7 w-7 mt-4 text-brand-green right-5 z-50 cursor-pointer"
                  @click="toggleShowPassword"
                />
                <EyeOffIcon
                  v-if="showPassword"
                  class="absolute h-7 w-7 mt-4 text-brand-dark-gray right-5 z-50 cursor-pointer"
                  @click="toggleShowPassword"
                />

                <SingleLineInput
                  id="password"
                  placeholder="Enter password here"
                  :type="showPassword ? 'text' : 'password'"
                  :value="password"
                  @input="updatePassword"
                ></SingleLineInput>
              </div>
            </div>

            <div class="mx-auto w-80 pt-5">
              <div class="py-2" v-if="errorMessage">
                <p class="text-brand-red text-sm">{{ errorMessage }}</p>
              </div>
              <div class="py-3">
                <PrimaryButton
                  @click="loginSubmit"
                  :disabled="!password || isAuthLoading || isApplicationLoading"
                  :is-loading="isAuthLoading || isApplicationLoading"
                  >Login and submit</PrimaryButton
                >
              </div>

              <div class="py-3">
                <SecondaryButton @click="setCurrentView('forgot_password')"
                  >I don't have a password</SecondaryButton
                >
              </div>
            </div>
          </SectionContainer>
        </template>
        <!-- END LOGIN SECTION -->

        <!-- START FORGOT PASSWORD SECTION -->
        <template v-if="currentView === 'forgot_password'">
          <SectionContainer v-if="getCurrentCheckedUsernameInfo">
            <h2 class="text-xl font-semibold my-3">No Password? We can send you a code</h2>
            <p class="">
              We can send you a temporary identification code to
              {{ getCurrentCheckedUsernameInfo.email }} to verify your account.
            </p>

            <div class="mx-auto w-80 pt-5">
              <div class="py-3">
                <PrimaryButton
                  @click="sendPasscode"
                  :disabled="isUserLoading"
                  :is-loading="isUserLoading"
                  >Send Code</PrimaryButton
                >
              </div>

              <div class="py-3">
                <SecondaryButton @click="setCurrentView('login')"
                  >Go back, I remember my password</SecondaryButton
                >
              </div>
            </div>
          </SectionContainer>
        </template>
        <!-- END FORGOT PASSWORD SECTION -->

        <!-- START INPUT PASSCODE SECTION -->
        <template v-if="currentView === 'input_passcode'">
          <SectionContainer v-if="getCurrentCheckedUsernameInfo">
            <h2 class="text-xl font-semibold my-3">We’ve sent you an identification code.</h2>
            <p class="">
              We’ve sent a temporary identification code to
              {{ getCurrentCheckedUsernameInfo.email }} to verify your account. Check your email and
              enter the code below.
            </p>

            <div class="py-3">
              <label class="block"> Temporary Identification Code </label>
              <SingleLineInput
                placeholder="Passcode"
                type="text"
                :value="passcode"
                @input="updatePasscode"
              ></SingleLineInput>
            </div>

            <div class="mx-auto w-80 pt-5">
              <div class="py-2" v-if="errorMessage">
                <p class="text-brand-red text-sm">{{ errorMessage }}</p>
              </div>
              <div class="py-3">
                <PrimaryButton
                  @click="confirmPasscode"
                  :disabled="isUserLoading"
                  :is-loading="isUserLoading"
                  >Next</PrimaryButton
                >
              </div>

              <div class="py-3">
                <SecondaryButton
                  @click="sendPasscode"
                  :disabled="isUserLoading"
                  :is-loading="isUserLoading"
                  >Resend passcode</SecondaryButton
                >
              </div>
            </div>
          </SectionContainer>
        </template>
        <!-- END INPUT PASSCODE SECTION -->
      </div>
    </div>
  </div>
</template>
<script>
// TODO: Refactor password validation to separate component
import { mapGetters } from "vuex"
import {
  APPLICATION_TEMPLATE_SUBMIT,
  USER_CHECK_USERNAME,
  USER_GET_PASSCODE,
  USER_CONFIRM_PASSCODE,
  AUTH_LOGIN,
  AUTH_LOGOUT
} from "@/store/types"

import { EyeIcon, EyeOffIcon } from "@heroicons/vue/outline"

export default {
  components: {
    EyeIcon,
    EyeOffIcon
  },
  computed: {
    ...mapGetters([
      "isAuthLoading",
      "isApplicationTemplateLoading",
      "getCurrentApplicationTemplate",
      "isApplicationLoading",
      "getCurrentApplication",
      "isUserLoading",
      "getCurrentCheckedUsernameInfo",
      "getCurrentUser"
    ]),
    applicantEmailFromForm() {
      if (!this.getCurrentApplicationTemplate) {
        return ""
      }
      let email
      this.getCurrentApplicationTemplate.form.sections.map((section) => {
        section.fields.map((field) => {
          if (field.name === "applicant_email") {
            email = field.value
          }
        })
      })
      return email
    },
    passwordValid() {
      return !!this.password && this.$refs.passwordGuide && this.$refs.passwordGuide.isValid
    }
  },
  data() {
    return {
      currentView: "create", //login, forgot_password, input_passcode, create_passcode
      email: "",
      password: "",
      passcode: "",
      showPassword: false,
      errorMessage: "",
      maxAttempts: 0,
      locked: false,
      wrongLogin: false,
      attemptsUsed: 0,
      showAuthError: false
    }
  },
  beforeMount() {
    if (!this.getCurrentApplicationTemplate) {
      this.$router.push({
        name: "apply_form",
        params: { slug: this.$route.params.slug }
      })
    }
  },
  async mounted() {
    this.email = this.applicantEmailFromForm

    if (this.applicantEmailFromForm) {
      try {
        const response = await this.$store.dispatch(USER_CHECK_USERNAME, {
          username: this.applicantEmailFromForm
        })

        if (!response.data.available) {
          this.currentView = "login"
        }
      } catch (err) {
        console.log(err)
      }
    }
  },
  methods: {
    setCurrentView(view) {
      this.currentView = view
    },
    updatePassword(ev) {
      this.password = ev.target.value
    },
    updateEmail(ev) {
      this.email = ev.target.value
    },
    updatePasscode(ev) {
      this.passcode = ev.target.value
    },
    toggleShowPassword() {
      this.showPassword = !this.showPassword
    },
    async sendPasscode() {
      const username = this.applicantEmailFromForm
      const that = this
      try {
        await this.$store.dispatch(USER_GET_PASSCODE, {
          username
        })
        if (this.currentView !== "input_passcode") {
          this.setCurrentView("input_passcode")
        }
        if (this.passcode) {
          this.passcode = ""
        }
      } catch (err) {
        this.errorMessage = "Failed to send passcode. Please try again."

        setTimeout(() => {
          that.errorMessage = ""
        }, 3000)
      }
    },
    async confirmPasscode() {
      const that = this

      try {
        await this.$store.dispatch(USER_CONFIRM_PASSCODE, {
          username: this.applicantEmailFromForm,
          passcode: this.passcode
        })
        this.setCurrentView("create_passcode")
      } catch (err) {
        this.errorMessage = "Invalid passcode. Please try sending another passcode."
        setTimeout(() => {
          that.errorMessage = ""
        }, 3000)
      }
    },
    async submit() {
      const slug = this.getCurrentApplicationTemplate.slug
      const data = {}

      this.getCurrentApplicationTemplate.form.sections.map((section) => {
        section.fields.map((field) => {
          if (field.value) data[field.name] = field.value

          if (field.field_id === "yes_no" && field.value) {
            const choice = field.choices.find((c) => c.label === field.value)

            data[field.name] = JSON.stringify({
              value: field.value,
              fields: choice.fields
            })
          }
        })
      })
      // Make sure to flush out logged in user if the user is trying to apply with different email
      if (this.getCurrentUser && this.getCurrentUser.username !== this.email) {
        await this.$store.dispatch(AUTH_LOGOUT)
      }

      if (this.email) {
        data.applicant_email = this.email
      }

      if (this.password) {
        data.applicant_password = this.password
      }

      const invite = localStorage.getItem("invite")

      if (invite) {
        data.invite = invite
        localStorage.removeItem("invite")
      }

      try {
        await this.$store.dispatch(APPLICATION_TEMPLATE_SUBMIT, { slug, data })

        const id = this.getCurrentApplication.id

        if (this.getCurrentApplicationTemplate.need_applicant_experian_authtoken) {
          this.$router.push({
            name: "apply_verify",
            params: { id }
          })
        } else if (this.getCurrentApplication.need_applicant_finicity_bank_connection) {
          this.$router.push({
            name: "apply_verify_income",
            params: { id }
          })
        } else if (parseFloat(this.getCurrentApplicationTemplate.price) > 0) {
          this.$router.push({
            name: "apply_pay",
            params: { id }
          })
        } 
        // else if (!this.getCurrentApplication.applicant.affiliate_offer_optin) {
        //   this.$router.push({
        //     name: "apply_affiliate_optin",
        //     params: { id: this.getCurrentApplication.id }
        //   })
        // } 
        else {
          this.$router.push({
            name: "apply_complete",
            params: { id }
          })
        }
      } catch (err) {
        const error = err
        console.log(error)
        if (err.response && err.response.data.error === "auth") {
          this.errorMessage = err.response.data.message
        } else {
          this.$router.push({
            name: "apply_form",
            params: { slug: this.getCurrentApplicationTemplate.slug }
          })
        }
      }
    },
    async loginSubmit() {
      const username = this.email.toLowerCase()
      const password = this.password
      const that = this

      if (!password) {
        this.errorMessage = "Password is required"
        return
      }

      try {
        await this.$store.dispatch(AUTH_LOGIN, { username, password })
      } catch (err) {
        const error = err.response.data

        if (error.locked) {
          if (error.locked === "True") {
            this.errorMessage = `No more than ${error.attempts_max} failed login attempts in a 30 minute period are allowed. Please try again later.`
          } else {
            this.errorMessage = `Sorry, the entered username or password is incorrect. For security reason, after ${parseInt(
              error.attempts_used
            )} more failed login attempts you'll have to wait 30 minutes before trying again.`
          }
        } else {
          this.errorMessage = error.message
        }

        setTimeout(() => {
          that.errorMessage = ""
        }, 3000)

        return
      }

      this.submit()
    },
    skipCreate() {
      this.submit()
    },
    createAccount() {
      this.submit()
    }
  }
}
</script>
